export type ProformaValidationIssue = {
  code: string;
  message: string;
  severity: "ERROR" | "WARNING";
};

export type ProformaValidationState = {
  status: "INVALIDE" | "INCOMPLET" | "PRET";
  isValid: boolean;
  errors: ProformaValidationIssue[];
  warnings: ProformaValidationIssue[];
};

type ProformaValidationLineInput = {
  description: string;
  quantite: number;
  prixUnitaire: number;
  remise: number;
};

type ProformaValidationInput = {
  clientId: string;
  typeMetier: string;
  objet: string;
  missionId?: number | "";
  commentaireMetier?: string;
  validiteProformaJours?: number | null;
  conditionsPaiement?: string;
  exonereTva: boolean;
  motifExoneration?: string;
  totalTTC: number;
  lignes: ProformaValidationLineInput[];
};

const COMMENT_REQUIRED_TYPES = new Set([
  "GROS_TRAVAUX",
  "CHANTIER",
  "URGENCE_PAYANTE",
]);

export function validateProformaDraft(
  input: ProformaValidationInput
): ProformaValidationState {
  const errors: ProformaValidationIssue[] = [];
  const warnings: ProformaValidationIssue[] = [];

  if (!input.clientId.trim()) {
    errors.push({
      code: "CLIENT_REQUIRED",
      message: "Client manquant.",
      severity: "ERROR",
    });
  }

  if (!input.typeMetier.trim()) {
    errors.push({
      code: "TYPE_METIER_REQUIRED",
      message: "Type métier manquant.",
      severity: "ERROR",
    });
  }

  if (!input.objet.trim()) {
    errors.push({
      code: "OBJET_REQUIRED",
      message: "Objet manquant.",
      severity: "ERROR",
    });
  }

  if (input.lignes.length === 0) {
    errors.push({
      code: "LINES_REQUIRED",
      message: "Aucune ligne de prestation.",
      severity: "ERROR",
    });
  }

  input.lignes.forEach((line, index) => {
    const row = index + 1;
    if (!line.description.trim()) {
      errors.push({
        code: `LINE_${row}_DESCRIPTION_REQUIRED`,
        message: `Ligne ${row}: description obligatoire.`,
        severity: "ERROR",
      });
    }

    if (!(line.quantite > 0)) {
      errors.push({
        code: `LINE_${row}_QTE_INVALID`,
        message: `Ligne ${row}: quantité doit être > 0.`,
        severity: "ERROR",
      });
    }

    if (line.prixUnitaire < 0) {
      errors.push({
        code: `LINE_${row}_PU_INVALID`,
        message: `Ligne ${row}: prix unitaire invalide (< 0).`,
        severity: "ERROR",
      });
    }

    if (line.prixUnitaire === 0) {
      warnings.push({
        code: `LINE_${row}_PU_ZERO`,
        message: `Ligne ${row}: prix unitaire à 0.`,
        severity: "WARNING",
      });
    }

    const montantBrut = Math.max(0, line.quantite) * Math.max(0, line.prixUnitaire);
    if (montantBrut > 0 && line.remise > montantBrut * 0.5) {
      warnings.push({
        code: `LINE_${row}_HIGH_DISCOUNT`,
        message: `Ligne ${row}: remise > 50% du montant de ligne.`,
        severity: "WARNING",
      });
    }
  });

  if (input.totalTTC <= 0) {
    errors.push({
      code: "TOTAL_TTC_INVALID",
      message: "Total TTC doit être strictement positif.",
      severity: "ERROR",
    });
  }

  if (input.exonereTva && !(input.motifExoneration ?? "").trim()) {
    errors.push({
      code: "EXONERATION_REASON_REQUIRED",
      message: "Exonération TVA active sans motif.",
      severity: "ERROR",
    });
  }

  const isTerrain = input.typeMetier.trim() !== "" && input.typeMetier !== "DEVIS_CLIENT";
  if (isTerrain && !input.missionId) {
    warnings.push({
      code: "TERRAIN_WITHOUT_LINKED_MISSION",
      message: "Type terrain sans mission/passage lié.",
      severity: "WARNING",
    });
  }

  if (
    COMMENT_REQUIRED_TYPES.has(input.typeMetier) &&
    !(input.commentaireMetier ?? "").trim()
  ) {
    warnings.push({
      code: "COMMENTAIRE_METIER_RECOMMENDED",
      message: "Commentaire métier recommandé pour ce type de devis.",
      severity: "WARNING",
    });
  }

  if (!input.validiteProformaJours || input.validiteProformaJours <= 0) {
    warnings.push({
      code: "VALIDITE_UNSET",
      message: "Validité proforma non définie.",
      severity: "WARNING",
    });
  }

  if (!(input.conditionsPaiement ?? "").trim()) {
    warnings.push({
      code: "CONDITIONS_PAIEMENT_MISSING",
      message: "Conditions de paiement absentes.",
      severity: "WARNING",
    });
  }

  if (errors.length > 0) {
    return { status: "INVALIDE", isValid: false, errors, warnings };
  }
  if (warnings.length > 0) {
    return { status: "INCOMPLET", isValid: true, errors, warnings };
  }
  return { status: "PRET", isValid: true, errors, warnings };
}
